import { TAG_PALETTE, syncRowTags } from "./candidate_row_sync";

export function initCandidateTags(root: ParentNode = document): void {
  const chips = root.querySelector<HTMLElement>("#tags-chips");
  if (!chips) return;

  const input = root.querySelector<HTMLInputElement>("#tag-input");
  const suggest = root.querySelector<HTMLElement>("#tag-suggest");
  const editable = !!(input && suggest);

  const url = chips.dataset.url || "";
  const TAG_DOT_PALETTE = TAG_PALETTE;
  const colorOffset = parseInt(chips.dataset.colorOffset || "0", 10) || 0;
  const botsProspectId = chips.dataset.bpId || "";
  const dotColor = (i: number): string => TAG_DOT_PALETTE[(colorOffset + i) % TAG_DOT_PALETTE.length];

  const parse = (s?: string): string[] => {
    try {
      const v = JSON.parse(s || "[]");
      return Array.isArray(v) ? v.map(String) : [];
    } catch {
      return [];
    }
  };

  let tags = parse(chips.dataset.tags);
  let suggestions = parse(chips.dataset.suggestions);

  const csrf = (): string =>
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

  const escHtml = (s: string): string => {
    const d = document.createElement("div");
    d.textContent = s;
    return d.innerHTML;
  };
  const escAttr = (s: string): string => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  const has = (list: string[], t: string): boolean =>
    list.some((x) => x.toLowerCase() === t.toLowerCase());

  function renderChips(): void {
    chips!.innerHTML = tags
      .map((t, i) => {
        const remove = editable
          ? `<span class="tx" data-tag="${escAttr(t)}" title="Remove tag">&times;</span>`
          : "";
        return (
          `<span class="ctag text-truncate" style="--tag-color:${escAttr(dotColor(i))}" title="${escAttr(t)}">` +
          `<span class="cdot2"></span>${escHtml(t)}${remove}</span>`
        );
      })
      .join("");
    syncRowTags(botsProspectId, tags, colorOffset);
  }

  function renderSuggest(): void {
    const q = input!.value.trim().toLowerCase();
    if (!q) {
      suggest!.classList.remove("open");
      suggest!.innerHTML = "";
      return;
    }
    const avail = suggestions
      .filter((s) => !has(tags, s) && s.toLowerCase().includes(q))
      .slice(0, 6);
    if (!avail.length) {
      suggest!.classList.remove("open");
      suggest!.innerHTML = "";
      return;
    }
    suggest!.innerHTML = avail
      .map((s) => `<div class="tags-suggest-opt" data-val="${escAttr(s)}">${escHtml(s)}</div>`)
      .join("");
    suggest!.classList.add("open");
  }

  async function persist(): Promise<void> {
    if (!url) return;
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf(), Accept: "application/json" },
        body: JSON.stringify({ tags }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      if (Array.isArray(data.tags)) {
        tags = data.tags.map(String);
        renderChips();
      }
    } catch {
      // Keep the optimistic UI; a later edit will retry the sync.
    }
  }

  function addTag(raw: string): void {
    const t = raw.trim();
    input!.value = "";
    suggest!.classList.remove("open");
    if (!t || has(tags, t)) {
      renderChips();
      return;
    }
    tags.push(t);
    if (!has(suggestions, t)) suggestions.push(t);
    renderChips();
    persist();
  }

  function removeTag(t: string): void {
    tags = tags.filter((x) => x !== t);
    renderChips();
    renderSuggest();
    persist();
  }

  if (editable) {
    chips.addEventListener("click", (e) => {
      const tx = (e.target as HTMLElement).closest<HTMLElement>(".tx");
      if (tx) removeTag(tx.dataset.tag || "");
    });

    input!.addEventListener("input", renderSuggest);
    input!.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addTag(input!.value);
      } else if (e.key === "Escape") {
        suggest!.classList.remove("open");
      } else if (e.key === "Backspace" && input!.value === "" && tags.length) {
        removeTag(tags[tags.length - 1]);
      }
    });

    suggest!.addEventListener("mousedown", (e) => {
      const opt = (e.target as HTMLElement).closest<HTMLElement>(".tags-suggest-opt");
      if (opt) {
        e.preventDefault();
        addTag(opt.dataset.val || "");
        input!.focus();
      }
    });
    input!.addEventListener("blur", () => {
      window.setTimeout(() => suggest!.classList.remove("open"), 120);
    });
  }

  renderChips();
}
